import { forwardRef, useCallback, useEffect, useId, useMemo, useState } from 'react';
import type { ZodSchema } from 'zod';
import { JsonEditor, type JsonEditorHandle, type JsonEditorProps } from '@/components/json-editor';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type JsonEditorStatus = 'empty' | 'valid' | 'invalid';

const STATUS_LABEL: Record<JsonEditorStatus, string> = {
  empty: 'empty (null)',
  valid: 'valid JSON',
  invalid: 'invalid',
};

const STATUS_CLASS: Record<JsonEditorStatus, string> = {
  empty: 'text-muted-foreground',
  valid: 'text-green-700 dark:text-green-400',
  invalid: 'text-destructive',
};

// True when `value` conveys "no content" structurally -- null/undefined or
// an empty container (`{}` / `[]`). Used to surface the same `empty (null)`
// status whether the user clears the field, leaves it untouched, or types
// an empty literal -- they all mean the same thing for a nullable JSONB
// column.
const isStructurallyEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value as object).length === 0;
  return false;
};

// Committed status is derived from the parent-known `value`, which is the
// last successful parse from the underlying JsonEditor (#346). It cannot
// observe transient syntax errors; that's what `liveInvalid` (below) is for.
//
// Order matters:
//   1. `null` / `undefined` is the canonical unset state and is always
//      "empty", regardless of schema (the JSONB column carries nullability,
//      not the inner schema).
//   2. Otherwise, schema rejection trumps "looks empty": a literal `{}`
//      that fails the schema is genuinely invalid, not a placeholder.
//   3. Only after passing the schema do we collapse `{}` / `[]` into
//      `empty`, matching the user's intuition that an empty container is
//      semantically equivalent to null.
const computeCommittedStatus = (
  value: unknown,
  schema: ZodSchema | undefined
): JsonEditorStatus => {
  if (value === null || value === undefined) return 'empty';
  if (schema !== undefined && !schema.safeParse(value).success) return 'invalid';
  if (isStructurallyEmpty(value)) return 'empty';
  return 'valid';
};

type FileFormatJsonEditorProps = {
  value: unknown;
  onChange: (value: unknown) => void;
  schema?: ZodSchema;
  label: string;
  helperText?: string;
  /**
   * Emitted whenever the editor's overall validity flips. False when the
   * inner textarea reports `aria-invalid="true"` (transient syntax errors,
   * schema failures) or when the committed value fails schema parsing.
   * Consumers can use this to gate Save/Submit buttons reactively.
   */
  onValidityChange?: (isValid: boolean) => void;
  /** Saved only when the user clicks Insert example. */
  example: unknown;
};

export const FileFormatJsonEditor = forwardRef<JsonEditorHandle, FileFormatJsonEditorProps>(
  function FileFormatJsonEditor(
    { value, onChange, schema, label, helperText, onValidityChange, example },
    ref
  ) {
    const fieldId = useId();

    const [liveInvalid, setLiveInvalid] = useState(false);
    // Remounts JsonEditor on insert. A parent value change does not replace a
    // textarea buffer that no longer matches the last committed value.
    const [editorInstance, setEditorInstance] = useState(0);

    const handleErrorsChange = useCallback((hasErrors: boolean) => {
      setLiveInvalid(hasErrors);
    }, []);

    const handleInsertExample = () => {
      setLiveInvalid(false);
      setEditorInstance((instance) => instance + 1);
      onChange(example);
    };

    // Insert example unmounts the button that held focus. Move focus to the
    // remounted textarea. Skip the initial render, when editorInstance is 0.
    useEffect(() => {
      if (editorInstance === 0) return;
      document.getElementById(fieldId)?.focus();
    }, [editorInstance, fieldId]);

    const committedStatus = useMemo(() => computeCommittedStatus(value, schema), [value, schema]);

    const status: JsonEditorStatus = liveInvalid ? 'invalid' : committedStatus;

    useEffect(() => {
      onValidityChange?.(status !== 'invalid');
    }, [status, onValidityChange]);

    // Forward helperText to the inner editor so it renders in the
    // canonical position (between Label and Textarea, per the base
    // JsonEditor contract from #346) and -- more importantly -- gets
    // wired into the textarea's `aria-describedby`. The status pill is
    // anchored to the relative container's top-right and aligns with
    // JsonEditor's label row, so this doesn't shift any layout.
    //
    // `exactOptionalPropertyTypes` disallows passing `undefined` for
    // optional props; build the inner props object so omitted fields
    // stay omitted.
    const innerProps: JsonEditorProps = {
      value,
      onChange,
      onErrorsChange: handleErrorsChange,
      label,
      id: fieldId,
      placeholder: 'null',
      ...(schema !== undefined ? { schema } : {}),
      ...(helperText !== undefined ? { helperText } : {}),
    };

    return (
      <div className="flex flex-col gap-2">
        <div className="relative">
          <span
            aria-live="polite"
            className={cn(
              'pointer-events-none absolute right-0 top-0 z-10 text-xs font-medium',
              STATUS_CLASS[status]
            )}
            data-testid={`${fieldId}-status`}
          >
            {STATUS_LABEL[status]}
          </span>
          <JsonEditor key={editorInstance} ref={ref} {...innerProps} />
        </div>
        {value === null || value === undefined ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={handleInsertExample}
            aria-label={`Insert example for ${label}`}
          >
            Insert example
          </Button>
        ) : null}
      </div>
    );
  }
);

FileFormatJsonEditor.displayName = 'FileFormatJsonEditor';
